/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { ActiveState, Jsonld, SeoProps } from "@eisinc/App/Widgets/SearchEngineOptimization/SearchEngineOptimization";
import { getSelectedBrandPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getAllBrandsDataView,
    getBrandCategoriesDataView,
    getBrandStateByPath,
} from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";

/**
 * Activate structure based on condition
 *
 * @param SeoProps
 * @returns ActiveState
 */
const activate = ({ state, payload, currentPage }: SeoProps): ActiveState => {
    const isBrandPage = currentPage === "Brands";
    const isBrandDetail = currentPage === "Brand Details";

    if (isBrandPage) {
        payload.brands = getAllBrandsDataView(state)?.value;
    }

    if (isBrandDetail) {
        const { pathname } = getLocation(state);
        const brandPath = getSelectedBrandPath(state) || pathname;
        const brandState = getBrandStateByPath(state, brandPath);
        if (!brandState.isLoading && brandState?.value) {
            payload.brands = [brandState.value];
        }

        const brandCategoriesParameter = {
            brandId: brandState.value?.id || "",
            page: 1,
            pageSize: 1000,
            sort: "name",
            maximumDepth: 3,
        };
        const brandCategoriesDataView = getBrandCategoriesDataView(state, brandCategoriesParameter);
        if (!brandCategoriesDataView.isLoading && brandCategoriesDataView?.value) {
            payload.brandCategories = brandCategoriesDataView.value;
        }
    }

    return (isBrandPage || isBrandDetail) && payload?.brands?.length;
};

/**
 * Build the structured data
 *
 * @param SeoProps
 * @returns Jsonld
 */
const json = ({ state, payload, url }: SeoProps): Jsonld => {
    const brands = payload.brands as BrandModel[];

    const brandSchema = brands?.map((brand: BrandModel, index: number) => {
        if (brand.logoSmallImagePath) {
            return {
                "@context": "https://schema.org",
                "@type": "Brand",
                name: brand.name,
                description: brand.name,
                logo: brand.logoSmallImagePath,
                image: brand.logoSmallImagePath,
                url: url + brand.detailPagePath,
            };
        }
        return {
            "@context": "https://schema.org",
            "@type": "Brand",
            name: brand.name,
            description: brand.name,
            url: url + brand.detailPagePath,
        };
    });

    const brandCategories = (payload?.brandCategories || [])?.map((item: any, index: number) => ({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: item.categoryName,
        description: item.shortDescription,
        image: item.featuredImagePath,
        url: url + item.productListPagePath,
    }));

    return {
        "@context": "https://schema.org",
        "@graph": [...brandSchema, ...brandCategories],
    };
};

export default { activate, json };
